// FORFEO INC. — Lot 4R-G
// Contrat pur de l'expérience utilisateur Forfeo Reserve.
// Reserve garde l'autorité des disponibilités et réservations.
// Giftshop garde l'autorité des montants, paiements, passes et QR.

export const FORFEO_RESERVE_USER_EXPERIENCE_SCHEMA =
  'forfeo_reserve_user_experience_v1' as const;

export const FORFEO_RESERVE_USER_LOCALES = [
  'fr-CA',
  'en-CA',
] as const;

export type ForfeoReserveUserLocale =
  typeof FORFEO_RESERVE_USER_LOCALES[number];

export const FORFEO_RESERVE_USER_ACTIONS = [
  'catalog',
  'resolve',
  'snapshot',
  'command',
  'calendar',
  'pass',
] as const;

export type ForfeoReserveUserAction =
  typeof FORFEO_RESERVE_USER_ACTIONS[number];

export const FORFEO_RESERVE_USER_COMMANDS = [
  'create_hold',
  'confirm_no_checkout',
  'join_waitlist',
  'cancel_booking',
  'request_reschedule',
  'resend_confirmation',
] as const;

export type ForfeoReserveUserCommandKind =
  typeof FORFEO_RESERVE_USER_COMMANDS[number];

export const FORFEO_RESERVE_USER_CONFIRMATION_MODES = [
  'free_booking',
  'pay_on_site',
  'deposit_required',
  'full_payment_required',
  'preauthorization_required',
] as const;

export type ForfeoReserveUserConfirmationMode =
  typeof FORFEO_RESERVE_USER_CONFIRMATION_MODES[number];

export const FORFEO_RESERVE_USER_AUTHORITIES = {
  discovery: 'EXPLORE',
  userInterface: 'EXPLORE',
  availability: 'RESERVE',
  holds: 'RESERVE',
  bookings: 'RESERVE',
  policies: 'RESERVE',
  cancellation: 'RESERVE',
  rescheduling: 'RESERVE',
  notifications: 'RESERVE',
  prices: 'GIFTSHOP',
  taxes: 'GIFTSHOP',
  fees: 'GIFTSHOP',
  payments: 'GIFTSHOP',
  refunds: 'GIFTSHOP',
  passes: 'GIFTSHOP',
  qr: 'GIFTSHOP',
} as const;

export interface ForfeoReserveUserOptionSelection {
  code: string;
  quantity: number;
}

export interface ForfeoReserveUserCatalogRequest {
  businessId: string;
  locale: ForfeoReserveUserLocale;
  correlationId: string;
}

export interface ForfeoReserveUserResolveRequest {
  businessId: string;
  catalogItemId: string;
  startsAt: string;
  endsAt: string;
  partySize: number;
  timezone: string;
  locale: ForfeoReserveUserLocale;
  optionSelections: ForfeoReserveUserOptionSelection[];
  correlationId: string;
}

export interface ForfeoReserveUserSnapshotRequest {
  businessId?: string;
  locale: ForfeoReserveUserLocale;
  correlationId: string;
}

export interface ForfeoReserveUserCommand {
  kind: ForfeoReserveUserCommandKind;
  businessId: string;
  catalogItemId?: string;
  holdId?: string;
  bookingId?: string;
  startsAt?: string;
  endsAt?: string;
  partySize?: number;
  expectedRevision?: number;
  locale: ForfeoReserveUserLocale;
  optionSelections?: ForfeoReserveUserOptionSelection[];
  idempotencyKey: string;
  correlationId: string;
}

export interface ForfeoReserveUserCalendarRequest {
  bookingId: string;
  locale: ForfeoReserveUserLocale;
  correlationId: string;
}

export interface ForfeoReserveUserPassReadRequest {
  schema: typeof FORFEO_RESERVE_USER_EXPERIENCE_SCHEMA;
  bookingId: string;
  businessId: string;
  sourceUserId: string;
  locale: ForfeoReserveUserLocale;
  correlationId: string;
}

export interface ForfeoReserveUserCatalogOption {
  code: string;
  label: string;
  description?: string;
  minimumQuantity: number;
  maximumQuantity: number;
  fulfillmentKind: 'operational' | 'giftshop';
}

export interface ForfeoReserveUserCatalogItem {
  id: string;
  label: string;
  description?: string;
  resourceKind: string;
  timezone: string;
  minimumPartySize: number;
  maximumPartySize: number;
  defaultDurationMinutes?: number;
  options: ForfeoReserveUserCatalogOption[];
}

export interface ForfeoReserveUserPolicySummary {
  resolutionState: 'resolved' | 'unresolved';
  confirmationMode?: ForfeoReserveUserConfirmationMode;
  financialHandoffRequired: boolean;
  cancellationDeadline?: string;
  modificationDeadline?: string;
  lateCancellationEffect?: string;
  noShowEffect?: string;
}

export interface ForfeoReserveUserResolution {
  authority: 'RESERVE';
  availability: {
    state: 'available' | 'limited' | 'fully_booked' | 'blocked' | 'closed';
    requestedCapacity: number;
    remainingCapacity: number;
    evaluatedAt: string;
  };
  policy: ForfeoReserveUserPolicySummary;
  optionSelections: ForfeoReserveUserOptionSelection[];
  correlationId: string;
}

export interface ForfeoReserveUserHold {
  id: string;
  businessId: string;
  catalogItemId: string;
  catalogItemLabel: string;
  startsAt: string;
  endsAt: string;
  partySize: number;
  status: string;
  expiresAt: string;
  revision: number;
  confirmationMode?: ForfeoReserveUserConfirmationMode;
  financialHandoffRequired: boolean;
  optionSelections: ForfeoReserveUserOptionSelection[];
}

export interface ForfeoReserveUserBooking {
  id: string;
  businessId: string;
  catalogItemId: string;
  catalogItemLabel: string;
  startsAt: string;
  endsAt: string;
  partySize: number;
  state: string;
  revision: number;
  confirmationMode?: ForfeoReserveUserConfirmationMode;
  cancellationDeadline?: string;
  modificationDeadline?: string;
  optionSelections: ForfeoReserveUserOptionSelection[];
  changeRequestState?: string;
}

export interface ForfeoReserveUserSnapshot {
  schema: typeof FORFEO_RESERVE_USER_EXPERIENCE_SCHEMA;
  authority: 'RESERVE';
  holds: ForfeoReserveUserHold[];
  bookings: ForfeoReserveUserBooking[];
  generatedAt: string;
}

export interface ForfeoReserveOfficialPassRead {
  available: boolean;
  authority: 'GIFTSHOP';
  passId?: string;
  passCode?: string;
  passUrl?: string;
  status?: string;
  errorCode?: string;
}

const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const SAFE_ID = /^[A-Za-z0-9][A-Za-z0-9:._/-]{0,199}$/;
const IDEMPOTENCY = /^[A-Za-z0-9][A-Za-z0-9:._/-]{15,199}$/;
const OPTION_CODE = /^[A-Za-z0-9][A-Za-z0-9:_-]{0,79}$/;

const FORBIDDEN_CLIENT_KEYS = new Set([
  'amount',
  'amount_cents',
  'amountcents',
  'price',
  'price_cents',
  'pricecents',
  'subtotal',
  'subtotal_cents',
  'subtotalcents',
  'total',
  'total_cents',
  'totalcents',
  'tax',
  'tax_cents',
  'taxcents',
  'fee',
  'fee_cents',
  'feecents',
  'deposit',
  'deposit_cents',
  'depositcents',
  'currency',
  'stripe_session_id',
  'stripesessionid',
  'stripe_payment_intent_id',
  'stripepaymentintentid',
  'stripe_refund_id',
  'striperefundid',
  'stripe_customer_id',
  'stripecustomerid',
  'qr_token',
  'qrtoken',
  'qr_value',
  'qrvalue',
  'pass_code',
  'passcode',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function validIso(value: unknown): boolean {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}

function validCorrelation(value: unknown): boolean {
  return typeof value === 'string'
    && value.trim().length >= 8
    && value.length <= 200;
}

export function normalizeForfeoReserveUserLocale(
  value: unknown,
): ForfeoReserveUserLocale {
  return String(value || '').toLowerCase().startsWith('en')
    ? 'en-CA'
    : 'fr-CA';
}

export function isForfeoReserveUserLocale(
  value: unknown,
): value is ForfeoReserveUserLocale {
  return FORFEO_RESERVE_USER_LOCALES.includes(
    value as ForfeoReserveUserLocale,
  );
}

export function isForfeoReserveUserAction(
  value: unknown,
): value is ForfeoReserveUserAction {
  return FORFEO_RESERVE_USER_ACTIONS.includes(
    value as ForfeoReserveUserAction,
  );
}

export function isForfeoReserveUserCommandKind(
  value: unknown,
): value is ForfeoReserveUserCommandKind {
  return FORFEO_RESERVE_USER_COMMANDS.includes(
    value as ForfeoReserveUserCommandKind,
  );
}

export function isForfeoReserveUserConfirmationMode(
  value: unknown,
): value is ForfeoReserveUserConfirmationMode {
  return FORFEO_RESERVE_USER_CONFIRMATION_MODES.includes(
    value as ForfeoReserveUserConfirmationMode,
  );
}

export function forfeoReserveUserModeRequiresGiftshop(
  value: ForfeoReserveUserConfirmationMode,
): boolean {
  return [
    'deposit_required',
    'full_payment_required',
    'preauthorization_required',
  ].includes(value);
}

export function hasForfeoReserveUserForbiddenClientFields(
  value: unknown,
): boolean {
  if (Array.isArray(value)) {
    return value.some(hasForfeoReserveUserForbiddenClientFields);
  }
  if (!isRecord(value)) return false;
  for (const [key, nested] of Object.entries(value)) {
    if (FORBIDDEN_CLIENT_KEYS.has(key.toLowerCase())) return true;
    if (hasForfeoReserveUserForbiddenClientFields(nested)) return true;
  }
  return false;
}

export function normalizeForfeoReserveUserOptionSelections(
  value: unknown,
): ForfeoReserveUserOptionSelection[] {
  if (!Array.isArray(value)) return [];
  const normalized = new Map<string, number>();
  for (const entry of value.slice(0, 20)) {
    if (!isRecord(entry)) continue;
    const code = String(entry.code || '').trim();
    const quantity = Number(entry.quantity);
    if (!OPTION_CODE.test(code)) continue;
    if (!Number.isSafeInteger(quantity) || quantity < 1 || quantity > 20) {
      continue;
    }
    normalized.set(code, Math.min(20, (normalized.get(code) || 0) + quantity));
  }
  return [...normalized.entries()]
    .map(([code, quantity]) => ({ code, quantity }))
    .sort((a, b) => a.code.localeCompare(b.code));
}

export function validateForfeoReserveUserCatalogRequest(
  request: ForfeoReserveUserCatalogRequest,
): readonly string[] {
  const errors: string[] = [];
  if (!request || typeof request !== 'object') return ['invalid_catalog_request'];
  if (!UUID.test(String(request.businessId || ''))) errors.push('invalid_business_id');
  if (!isForfeoReserveUserLocale(request.locale)) errors.push('invalid_locale');
  if (!validCorrelation(request.correlationId)) errors.push('invalid_correlation_id');
  if (hasForfeoReserveUserForbiddenClientFields(request)) {
    errors.push('financial_fields_forbidden');
  }
  return errors;
}

export function validateForfeoReserveUserResolveRequest(
  request: ForfeoReserveUserResolveRequest,
): readonly string[] {
  const errors: string[] = [];
  if (!request || typeof request !== 'object') return ['invalid_resolve_request'];
  if (!UUID.test(String(request.businessId || ''))) errors.push('invalid_business_id');
  if (!UUID.test(String(request.catalogItemId || ''))) errors.push('invalid_catalog_item_id');
  if (!validIso(request.startsAt) || !validIso(request.endsAt)) {
    errors.push('invalid_booking_window');
  } else {
    const duration = Date.parse(request.endsAt) - Date.parse(request.startsAt);
    if (duration <= 0 || duration > 31 * 24 * 60 * 60 * 1000) {
      errors.push('invalid_booking_window');
    }
  }
  if (!Number.isSafeInteger(request.partySize)
      || request.partySize < 1
      || request.partySize > 100000) {
    errors.push('invalid_party_size');
  }
  if (typeof request.timezone !== 'string'
      || request.timezone.length < 3
      || request.timezone.length > 100) {
    errors.push('invalid_timezone');
  }
  if (!isForfeoReserveUserLocale(request.locale)) errors.push('invalid_locale');
  if (!validCorrelation(request.correlationId)) errors.push('invalid_correlation_id');
  const normalized = normalizeForfeoReserveUserOptionSelections(
    request.optionSelections,
  );
  if (normalized.length !== request.optionSelections.length) {
    errors.push('invalid_option_selections');
  }
  if (hasForfeoReserveUserForbiddenClientFields(request)) {
    errors.push('financial_fields_forbidden');
  }
  return [...new Set(errors)];
}

export function validateForfeoReserveUserSnapshotRequest(
  request: ForfeoReserveUserSnapshotRequest,
): readonly string[] {
  const errors: string[] = [];
  if (!request || typeof request !== 'object') return ['invalid_snapshot_request'];
  if (request.businessId && !UUID.test(request.businessId)) {
    errors.push('invalid_business_id');
  }
  if (!isForfeoReserveUserLocale(request.locale)) errors.push('invalid_locale');
  if (!validCorrelation(request.correlationId)) errors.push('invalid_correlation_id');
  if (hasForfeoReserveUserForbiddenClientFields(request)) {
    errors.push('financial_fields_forbidden');
  }
  return errors;
}

export function validateForfeoReserveUserCommand(
  command: ForfeoReserveUserCommand,
): readonly string[] {
  const errors: string[] = [];
  if (!command || typeof command !== 'object') return ['invalid_user_command'];
  if (!isForfeoReserveUserCommandKind(command.kind)) errors.push('invalid_command_kind');
  if (!UUID.test(String(command.businessId || ''))) errors.push('invalid_business_id');
  if (!IDEMPOTENCY.test(String(command.idempotencyKey || ''))) {
    errors.push('invalid_idempotency_key');
  }
  if (!validCorrelation(command.correlationId)) errors.push('invalid_correlation_id');
  if (!isForfeoReserveUserLocale(command.locale)) errors.push('invalid_locale');
  if (hasForfeoReserveUserForbiddenClientFields(command)) {
    errors.push('financial_fields_forbidden');
  }

  if (['create_hold', 'join_waitlist'].includes(command.kind)) {
    if (!UUID.test(String(command.catalogItemId || ''))) {
      errors.push('catalog_item_id_required');
    }
    if (!validIso(command.startsAt) || !validIso(command.endsAt)
        || Date.parse(String(command.startsAt)) >= Date.parse(String(command.endsAt))) {
      errors.push('invalid_booking_window');
    }
    if (!Number.isSafeInteger(command.partySize) || Number(command.partySize) < 1) {
      errors.push('invalid_party_size');
    }
  }

  if (command.kind === 'create_hold') {
    const selections = command.optionSelections || [];
    if (normalizeForfeoReserveUserOptionSelections(selections).length !== selections.length) {
      errors.push('invalid_option_selections');
    }
  }

  if (command.kind === 'confirm_no_checkout') {
    if (!UUID.test(String(command.holdId || ''))) errors.push('hold_id_required');
  }

  if ([
    'cancel_booking',
    'request_reschedule',
    'resend_confirmation',
  ].includes(command.kind)) {
    if (!UUID.test(String(command.bookingId || ''))) errors.push('booking_id_required');
  }

  if (command.kind === 'request_reschedule') {
    if (!validIso(command.startsAt) || !validIso(command.endsAt)
        || Date.parse(String(command.startsAt)) >= Date.parse(String(command.endsAt))) {
      errors.push('invalid_booking_window');
    }
  }

  if ([
    'confirm_no_checkout',
    'cancel_booking',
    'request_reschedule',
  ].includes(command.kind)
      && (!Number.isSafeInteger(command.expectedRevision)
        || Number(command.expectedRevision) < 1)) {
    errors.push('expected_revision_required');
  }

  return [...new Set(errors)];
}

export function validateForfeoReserveUserCalendarRequest(
  request: ForfeoReserveUserCalendarRequest,
): readonly string[] {
  const errors: string[] = [];
  if (!request || typeof request !== 'object') return ['invalid_calendar_request'];
  if (!UUID.test(String(request.bookingId || ''))) errors.push('invalid_booking_id');
  if (!isForfeoReserveUserLocale(request.locale)) errors.push('invalid_locale');
  if (!validCorrelation(request.correlationId)) errors.push('invalid_correlation_id');
  if (hasForfeoReserveUserForbiddenClientFields(request)) {
    errors.push('financial_fields_forbidden');
  }
  return errors;
}

export function validateForfeoReserveUserPassReadRequest(
  request: ForfeoReserveUserPassReadRequest,
): readonly string[] {
  const errors: string[] = [];
  if (!request || typeof request !== 'object') return ['invalid_pass_request'];
  if (request.schema !== FORFEO_RESERVE_USER_EXPERIENCE_SCHEMA) {
    errors.push('invalid_schema');
  }
  if (!UUID.test(String(request.bookingId || ''))) errors.push('invalid_booking_id');
  if (!UUID.test(String(request.businessId || ''))) errors.push('invalid_business_id');
  if (!UUID.test(String(request.sourceUserId || ''))) errors.push('invalid_source_user_id');
  if (!isForfeoReserveUserLocale(request.locale)) errors.push('invalid_locale');
  if (!validCorrelation(request.correlationId)) errors.push('invalid_correlation_id');
  if (hasForfeoReserveUserForbiddenClientFields(request)) {
    errors.push('financial_fields_forbidden');
  }
  return errors;
}

export function createForfeoReserveUserCalendarContent(input: {
  bookingId: string;
  title: string;
  description: string;
  startsAt: string;
  endsAt: string;
  timezone: string;
  locale: ForfeoReserveUserLocale;
  url?: string;
}): string {
  if (!UUID.test(input.bookingId)) throw new Error('invalid_booking_id');
  if (!validIso(input.startsAt) || !validIso(input.endsAt)) {
    throw new Error('invalid_calendar_window');
  }
  const escape = (value: string) => value
    .replaceAll('\\', '\\\\')
    .replaceAll('\n', '\\n')
    .replaceAll(',', '\\,')
    .replaceAll(';', '\\;');
  const utc = (value: string) => new Date(value)
    .toISOString()
    .replaceAll('-', '')
    .replaceAll(':', '')
    .replace('.000', '');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//FORFEO INC.//Forfeo Reserve//FR',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:reserve-${input.bookingId}@forfeo.com`,
    `DTSTAMP:${utc(new Date().toISOString())}`,
    `DTSTART:${utc(input.startsAt)}`,
    `DTEND:${utc(input.endsAt)}`,
    `SUMMARY:${escape(input.title)}`,
    `DESCRIPTION:${escape(input.description)}`,
    `X-FORFEO-TIMEZONE:${escape(input.timezone)}`,
    `X-FORFEO-LOCALE:${input.locale}`,
    ...(input.url ? [`URL:${escape(input.url)}`] : []),
    'END:VEVENT',
    'END:VCALENDAR',
    '',
  ];
  return lines.join('\r\n');
}

export function assertForfeoReserveUserExperienceBoundary(): true {
  if (
    FORFEO_RESERVE_USER_AUTHORITIES.availability !== 'RESERVE'
    || FORFEO_RESERVE_USER_AUTHORITIES.bookings !== 'RESERVE'
    || FORFEO_RESERVE_USER_AUTHORITIES.payments !== 'GIFTSHOP'
    || FORFEO_RESERVE_USER_AUTHORITIES.passes !== 'GIFTSHOP'
    || FORFEO_RESERVE_USER_AUTHORITIES.qr !== 'GIFTSHOP'
  ) {
    throw new Error('forfeo_reserve_user_experience_boundary_violation');
  }
  return true;
}

export function isSafeForfeoReserveUserIdentifier(value: unknown): boolean {
  return SAFE_ID.test(String(value || ''));
}
